import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import './attendanceRecords.css';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faChartLine,
  faFilter,
  faSearch,
  faRefresh,
  faFilePdf,
  faFileExcel,
  faFileCsv,
  faPrint,
  faCalendarDay,
  faUsers,
  faChartBar,
  faArrowLeft,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faCalendar,
  faSort,
  faTrash,
  faEye,
  faEdit,
  faDatabase,
  faClipboardList,
  faChartPie,
  faSchool,
  faUserGraduate,
  faIdCard,
  faMobileAlt,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faExclamationCircle,
  faPercentage,
  faUserCheck,
  faCalendarCheck,
  faArrowUp,
  faArrowDown,
  faFilterCircleXmark,
  faBars,
  faBuilding,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faSignature
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';

const PHP_API_BASE = "https://mhsdev.online/MHS-WEB/api/ManageAttendance.php";

// Utility function to generate base64 from SVG
const svgToBase64 = (svg) => {
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

// Add watermark to PDF
const addWatermark = (doc, text) => {
  const pageCount = doc.internal.getNumberOfPages();
  const watermarkOpacity = 0.03;
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(80);
    doc.setGState(doc.GState({ opacity: watermarkOpacity }));
    
    // Center the watermark
    const textWidth = doc.getTextWidth(text);
    const x = (doc.internal.pageSize.width - textWidth) / 2;
    const y = doc.internal.pageSize.height / 2;
    
    doc.text(text, x, y);
    doc.setGState(doc.GState({ opacity: 1 }));
  }
};

// Generate school logo SVG
const generateSchoolLogo = (schoolName) => {
  return `<svg width="150" height="40" xmlns="http://www.w3.org/2000/svg">
    <rect width="150" height="40" rx="5" fill="#4f46e5"/>
    <text x="75" y="25" font-family="Arial, sans-serif" font-size="14" font-weight="bold" 
          fill="white" text-anchor="middle" dominant-baseline="middle">
      ${schoolName}
    </text>
  </svg>`;
};

const AttendanceRecords = () => {
  const navigate = useNavigate();
  
  // State management
  const [filters, setFilters] = useState({
    class: '',
    section: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    month: '',
    year: new Date().getFullYear().toString(),
    studentId: '',
    studentName: '',
    familyStatus: 'All',
    attendanceStatus: 'All',
    page: 1,
    limit: 25,
    sortBy: 'marked_at',
    sortOrder: 'desc'
  });
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statistics, setStatistics] = useState({
    total: {
      unique_students: 0,
      attendance_rate: 0,
      total_absences: 0,
      total_classes: 0,
      total_sections: 0
    }
  });
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [recordToMark, setRecordToMark] = useState(null);
  
  // School information for PDF
  const schoolInfo = {
    name: "MODERN HIGH SCHOOL",
    address: "123 Education Street, Knowledge City",
    phone: "+1 (555) 123-4567",
    email: "info@modernhigh.edu",
    website: "www.modernhigh.edu"
  };
  
  // Chart data
  const [chartData, setChartData] = useState({
    dailyTrend: [],
    classDistribution: [],
    statusDistribution: []
  });

  // Initialize
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters(prev => ({
      ...prev,
      startDate: formatDate(firstDayOfMonth),
      endDate: formatDate(lastDayOfMonth),
      month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      year: today.getFullYear().toString()
    }));
    
    fetchStatistics();
    fetchAvailableClasses();
    fetchChartData();
    fetchAttendanceRecords();
  }, []);

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${PHP_API_BASE}?action=get-attendance-statistics`);
      const data = await response.json();
      
      if (data.success) {
        setStatistics(data.statistics || data);
      } else {
        // Set default statistics
        setStatistics({
          total: {
            unique_students: 0,
            attendance_rate: 0,
            total_absences: 0,
            total_classes: 0,
            total_sections: 0
          }
        });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setStatistics({
        total: {
          unique_students: 0,
          attendance_rate: 0,
          total_absences: 0,
          total_classes: 0,
          total_sections: 0
        }
      });
    }
  };

  // Fetch chart data
  const fetchChartData = async () => {
    try {
      const response = await fetch(`${PHP_API_BASE}?action=get-attendance-statistics`);
      const data = await response.json();
      
      if (data.success) {
        // Process data for charts
        const trendData = data.statistics?.trend || [];
        const classDist = data.statistics?.top_classes || [];
        
        setChartData({
          dailyTrend: trendData.map(item => ({
            date: new Date(item.date).toLocaleDateString('en-US', { weekday: 'short' }),
            absences: item.absences || 0,
            students: item.unique_students || 0
          })),
          classDistribution: classDist.map(item => ({
            name: item.class,
            value: item.absences || 0
          })),
          statusDistribution: [
            { name: 'Absent', value: data.statistics?.total?.total_absences || 0, color: '#ef4444' },
            { name: 'Present', value: 100, color: '#10b981' }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      // Set default chart data
      setChartData({
        dailyTrend: [
          { date: 'Mon', absences: 12, students: 10 },
          { date: 'Tue', absences: 8, students: 8 },
          { date: 'Wed', absences: 15, students: 12 },
          { date: 'Thu', absences: 10, students: 9 },
          { date: 'Fri', absences: 18, students: 15 }
        ],
        classDistribution: [
          { name: 'Class 10', value: 25 },
          { name: 'Class 9', value: 20 },
          { name: 'Class 8', value: 18 },
          { name: 'Class 7', value: 15 },
          { name: 'Class 6', value: 12 }
        ],
        statusDistribution: [
          { name: 'Absent', value: 90, color: '#ef4444' },
          { name: 'Present', value: 910, color: '#10b981' }
        ]
      });
    }
  };

  // Fetch available classes
  const fetchAvailableClasses = async () => {
    try {
      const response = await fetch(`${PHP_API_BASE}?action=get-all-classes`);
      const data = await response.json();
      
      if (data.success) {
        setAvailableClasses(data.classes || []);
      } else {
        setAvailableClasses(['Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10']);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setAvailableClasses(['Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10']);
    }
  };

  // Fetch sections
  const fetchSectionsByClass = async (className) => {
    if (!className) {
      setAvailableSections([]);
      return;
    }
    
    try {
      const response = await fetch(
        `${PHP_API_BASE}?action=get-sections-by-class&className=${encodeURIComponent(className)}`
      );
      const data = await response.json();
      
      if (data.success) {
        setAvailableSections(data.sections || []);
      } else {
        setAvailableSections(['A', 'B', 'C', 'D']);
      }
    } catch (error) {
      console.error('Error fetching sections:', error);
      setAvailableSections(['A', 'B', 'C', 'D']);
    }
  };

  // Fetch attendance records
  // Replace your existing fetchAttendanceRecords function with this
    const fetchAttendanceRecords = async () => {
        setLoading(true);
        
        try {
          const params = new URLSearchParams();
          
          // ... (keep your existing params appending logic here) ...
          // Copy lines 312-324 from your code here
          if (filters.class && filters.class !== 'All') params.append('class', filters.class);
          if (filters.section && filters.section !== 'All') params.append('section', filters.section);
          if (filters.startDate) params.append('start_date', filters.startDate);
          if (filters.endDate) params.append('end_date', filters.endDate);
          // ... etc ...

          console.log("Fetching URL:", `${PHP_API_BASE}?action=get-all-attendance-records&${params.toString()}`);

          const response = await fetch(
            `${PHP_API_BASE}?action=get-all-attendance-records&${params.toString()}`
          );
          
          const data = await response.json();
          
          // --- DEBUGGING LOG ---
          console.log("Full API Response:", data); 
          // check your browser console (F12) to see what 'data' actually looks like
          
          if (data.success) {
            // FIX: Check multiple common property names
            const fetchedRecords = data.data || data.records || data.attendance || [];
            
            console.log("Records to set:", fetchedRecords);

            setRecords(fetchedRecords);
            setTotalRecords(data.total || fetchedRecords.length || 0);
            setTotalPages(data.pagination?.total_pages || 1);
            
            if (fetchedRecords.length > 0) {
              Swal.fire({
                icon: 'success',
                title: `Found ${fetchedRecords.length} records`,
                timer: 1500,
                showConfirmButton: false
              });
            }
          } else {
            setRecords([]);
            setTotalRecords(0);
            setTotalPages(1);
          }
        } catch (error) {
          console.error('Error fetching records:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to fetch records',
            confirmButtonColor: '#ef4444'
          });
        } finally {
          setLoading(false);
        }
    };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value, page: 1 };
      
      if (key === 'class') {
        fetchSectionsByClass(value);
        newFilters.section = '';
      }
      
      return newFilters;
    });
  };

  // Apply filters
  const handleApplyFilters = () => {
    fetchAttendanceRecords();
  };

  // Reset filters
  const handleResetFilters = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
      class: '',
      section: '',
      startDate: formatDate(firstDayOfMonth),
      endDate: formatDate(lastDayOfMonth),
      month: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      year: today.getFullYear().toString(),
      studentId: '',
      studentName: '',
      familyStatus: 'All',
      attendanceStatus: 'All',
      page: 1,
      limit: 25,
      sortBy: 'marked_at',
      sortOrder: 'desc'
    });
    
    setAvailableSections([]);
    setSelectedRecords([]);
  };

  // Handle sort
  const handleSort = (key) => {
    const direction = filters.sortBy === key && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters(prev => ({ ...prev, sortBy: key, sortOrder: direction, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // ULTRA-PROFESSIONAL PDF EXPORT FUNCTION
  const exportToPDF = async () => {
    setExporting(true);
    try {
      // Initialize jsPDF with A4 size
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 15;
      let yPos = margin;
      
      // Add watermark
      doc.setGState(new doc.GState({ opacity: 0.03 }));
      doc.setFontSize(80);
      doc.setTextColor(200, 200, 200);
      doc.text('CONFIDENTIAL', pageWidth / 2, pageHeight / 2, { align: 'center', angle: 45 });
      doc.setGState(new doc.GState({ opacity: 1 }));
      
      // School Header with gradient background simulation
      doc.setFillColor(79, 70, 229); // Primary color
      doc.rect(0, 0, pageWidth, 30, 'F');
      
      // School Name
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(schoolInfo.name, pageWidth / 2, 18, { align: 'center' });
      
      // School motto/subtitle
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Excellence in Education Since 1995', pageWidth / 2, 25, { align: 'center' });
      
      yPos = 40;
      
      // Report Title
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('ATTENDANCE RECORDS REPORT', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Report Details - Convert all to strings
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      
      // Convert dates to strings
      const startDateStr = formatDisplayDate(filters.startDate);
      const endDateStr = formatDisplayDate(filters.endDate);
      const generatedDateStr = new Date().toLocaleString();
      const reportIdStr = `ATT-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
      
      doc.text(`Report Period: ${startDateStr} to ${endDateStr}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      doc.text(`Generated: ${generatedDateStr}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      doc.text(`Report ID: ${reportIdStr}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Horizontal line separator
      doc.setDrawColor(79, 70, 229);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;
      
      // Executive Summary Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('EXECUTIVE SUMMARY', margin, yPos);
      yPos += 8;
      
      // Summary boxes - Ensure all values are strings
      const summaryData = [
        { 
          label: 'Total Students', 
          value: String(statistics.total?.unique_students || '0'), 
          icon: faUsers, 
          color: '#4f46e5' 
        },
        { 
          label: 'Attendance Rate', 
          value: String(`${statistics.total?.attendance_rate || '0'}%`), 
          icon: faCheckCircle, 
          color: '#10b981' 
        },
        { 
          label: 'Total Absences', 
          value: String(statistics.total?.total_absences || '0'), 
          icon: faTimesCircle, 
          color: '#ef4444' 
        },
        { 
          label: 'Active Classes', 
          value: String(statistics.total?.total_classes || '0'), 
          icon: faSchool, 
          color: '#3b82f6' 
        }
      ];
      
      const boxWidth = (pageWidth - (2 * margin) - 15) / 4;
      const boxHeight = 25;
      let xPos = margin;
      
      summaryData.forEach((item, index) => {
        // Draw box with border
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(250, 250, 250);
        doc.roundedRect(xPos, yPos, boxWidth, boxHeight, 2, 2, 'FD');
        
        // Add colored circle for icon
        doc.setFillColor(item.color);
        doc.circle(xPos + 10, yPos + 12, 4, 'F');
        
        // Label - Ensure string
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'normal');
        doc.text(String(item.label), xPos + 20, yPos + 8);
        
        // Value - Ensure string
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(String(item.value), xPos + 20, yPos + 18);
        
        xPos += boxWidth + 5;
      });
      
      yPos += boxHeight + 10;
      
      // Filter Criteria Section - Build string safely
      if (filters.class || filters.section || filters.studentId || filters.studentName) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('FILTER CRITERIA', margin, yPos);
        yPos += 6;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        
        const criteriaParts = [];
        if (filters.class) criteriaParts.push(`Class: ${String(filters.class)}`);
        if (filters.section) criteriaParts.push(`Section: ${String(filters.section)}`);
        if (filters.studentId) criteriaParts.push(`Student ID: ${String(filters.studentId)}`);
        if (filters.studentName) criteriaParts.push(`Name: ${String(filters.studentName)}`);
        if (filters.familyStatus !== 'All') criteriaParts.push(`Family Status: ${String(filters.familyStatus)}`);
        
        if (criteriaParts.length > 0) {
          const criteriaText = criteriaParts.join(' | ');
          // Split long text into multiple lines if needed
          const maxWidth = pageWidth - 2 * margin;
          const lines = doc.splitTextToSize(criteriaText, maxWidth);
          lines.forEach((line, index) => {
            doc.text(line, margin, yPos + (index * 4));
          });
          yPos += (lines.length * 4) + 5;
        }
      }
      
      // Table Section
      yPos += 5;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('ATTENDANCE RECORDS', margin, yPos);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      
      // Convert page info to strings
      const totalRecordsStr = String(totalRecords);
      const pageStr = String(filters.page);
      const totalPagesStr = String(totalPages);
      doc.text(`Total Records: ${totalRecordsStr} | Page ${pageStr} of ${totalPagesStr}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 5;
      
      // Prepare table data - Ensure all values are strings
      const tableData = records.map(record => [
        String(record.student_id || ''),
        String(record.name || ''),
        String(record.father_name || 'N/A'),
        String(record.class || ''),
        String(record.section || 'N/A'),
        String(record.family_status || 'N/A'),
        String(formatDisplayDate(record.marked_at) || ''),
        String(record.mobile || 'N/A'),
        String(record.day || '')
      ]);
      
      // Create professional table with autoTable
      autoTable(doc, {
        startY: yPos,
        head: [[
          'Student ID', 
          'Name', 
          'Father\'s Name', 
          'Class', 
          'Section', 
          'Family Status',
          'Date', 
          'Mobile', 
          'Day'
        ]],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: 'linebreak',
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
          valign: 'middle',
          halign: 'left'
        },
        headStyles: {
          fillColor: [79, 70, 229],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 4,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250]
        },
        columnStyles: {
          0: { cellWidth: 25, fontStyle: 'bold' },
          1: { cellWidth: 30 },
          2: { cellWidth: 30 },
          3: { cellWidth: 20 },
          4: { cellWidth: 20 },
          5: { cellWidth: 25 },
          6: { cellWidth: 25 },
          7: { cellWidth: 25 },
          8: { cellWidth: 20 }
        },
        didDrawPage: function(data) {
          // Footer on each page
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.setFont('helvetica', 'normal');
          
          // Page number - Convert to string
          const pageCount = doc.internal.getNumberOfPages();
          doc.text(`Page ${String(data.pageNumber)} of ${String(pageCount)}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          
          // Confidential footer
          doc.text(`Confidential - ${schoolInfo.name} - ${schoolInfo.website}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
          
          // Top border on continuation pages
          if (data.pageNumber > 1) {
            doc.setDrawColor(79, 70, 229);
            doc.setLineWidth(0.5);
            doc.line(margin, 10, pageWidth - margin, 10);
            
            // Continued header
            doc.setFontSize(10);
            doc.setTextColor(79, 70, 229);
            doc.setFont('helvetica', 'bold');
            doc.text('ATTENDANCE RECORDS REPORT (Continued)', pageWidth / 2, 20, { align: 'center' });
          }
        }
      });
      
      // Get final Y position after table
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : yPos + 150;
      
      // Add notes section if space permits
      if (pageHeight - finalY > 40) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont('helvetica', 'italic');
        
        const notes = [
          'Notes:',
          '1. This report contains confidential student attendance information.',
          '2. All data is accurate as of the generation date.',
          '3. For official verification, contact the school administration.',
          '4. Unauthorized distribution is prohibited.'
        ];
        
        notes.forEach((note, index) => {
          doc.text(String(note), margin, finalY + 10 + (index * 4));
        });
      }
      
      // Add signature section
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      
      doc.text('Prepared by:', pageWidth - 100, pageHeight - 25);
      doc.setLineWidth(0.5);
      doc.line(pageWidth - 100, pageHeight - 20, pageWidth - 30, pageHeight - 20);
      doc.text('School Administrator', pageWidth - 65, pageHeight - 15, { align: 'center' });
      
      // Save the PDF with timestamp
      const timestamp = new Date().toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .split('.')[0];
      
      const filename = `Attendance_Report_${timestamp}.pdf`;
      doc.save(filename);
      
      // Show success notification
      Swal.fire({
        icon: 'success',
        title: 'Professional PDF Generated!',
        html: `
          <div style="text-align: left; padding: 20px;">
            <p><strong>Report Details:</strong></p>
            <ul style="text-align: left; margin-left: 20px;">
              <li>Total Records: ${totalRecords}</li>
              <li>Report Period: ${formatDisplayDate(filters.startDate)} to ${formatDisplayDate(filters.endDate)}</li>
              <li>Generated: ${new Date().toLocaleString()}</li>
              <li>Filename: ${filename}</li>
            </ul>
            <p style="color: #4f46e5; font-weight: bold;">✓ Professional A4 format with watermarks</p>
            <p style="color: #4f46e5; font-weight: bold;">✓ Executive summary and statistics</p>
            <p style="color: #4f46e5; font-weight: bold;">✓ Multiple page support with headers</p>
          </div>
        `,
        confirmButtonText: 'Open Download Folder',
        confirmButtonColor: '#4f46e5',
        showCancelButton: true,
        cancelButtonText: 'Close',
        width: 600
      }).then((result) => {
        if (result.isConfirmed) {
          // Try to open downloads folder
          try {
            const a = document.createElement('a');
            a.href = '#';
            a.click();
          } catch (e) {
            console.log('Cannot open downloads folder directly');
          }
        }
      });
      
    } catch (error) {
      console.error('PDF Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to generate professional PDF report',
        html: `
          <div style="text-align: left; padding: 10px;">
            <p>Error details: ${error.message}</p>
            <p style="color: #ef4444; font-size: 12px;">
              <strong>Possible solutions:</strong><br/>
              1. Check browser permissions<br/>
              2. Try with fewer records<br/>
              3. Ensure javascript is enabled
            </p>
          </div>
        `,
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    // Create CSV content
    const headers = ['Student ID,Name,Father Name,Class,Section,Family Status,Date,Day,Mobile'];
    const rows = records.map(record => 
      `"${record.student_id}","${record.name}","${record.father_name || ''}","${record.class}","${record.section || ''}","${record.family_status || ''}","${record.marked_at}","${record.day}","${record.mobile || ''}"`
    );
    
    const csvContent = [...headers, ...rows].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `attendance-records-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    Swal.fire({
      icon: 'success',
      title: 'Export Successful',
      text: 'CSV file has been downloaded',
      confirmButtonColor: '#10b981'
    });
  };

  // View record details
  const openViewModal = (record) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
  };

  // Mark student as present (delete from absent_students)
  const openMarkPresentModal = (record) => {
    setRecordToMark(record);
    setConfirmModalOpen(true);
  };

  // Confirm mark present
  const confirmMarkPresent = async () => {
    if (!recordToMark) return;
    
    try {
      const formData = new FormData();
      formData.append('action', 'delete-absent-record');
      formData.append('recordId', recordToMark.id);
      
      const response = await fetch(PHP_API_BASE, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Student marked as present and absent record removed',
          confirmButtonColor: '#10b981'
        });
        
        // Refresh the records
        fetchAttendanceRecords();
        // Refresh statistics
        fetchStatistics();
        // Refresh chart data
        fetchChartData();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.error || 'Failed to mark student as present',
          confirmButtonColor: '#ef4444'
        });
      }
    } catch (error) {
      console.error('Error marking present:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to mark student as present',
        confirmButtonColor: '#ef4444'
      });
    } finally {
      setConfirmModalOpen(false);
      setRecordToMark(null);
    }
  };

  // Select/deselect all records
  const toggleSelectAll = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(record => record.id));
    }
  };

  // Toggle single record selection
  const toggleRecordSelection = (id) => {
    setSelectedRecords(prev => 
      prev.includes(id) 
        ? prev.filter(recordId => recordId !== id)
        : [...prev, id]
    );
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (filters.sortBy !== key) return faSort;
    return filters.sortOrder === 'asc' ? faArrowUp : faArrowDown;
  };

  // Navigate back
  const handleBackClick = () => {
    navigate('/Manage-Classes-Attendance');
  };

  // Load data on filter changes
  useEffect(() => {
    fetchAttendanceRecords();
  }, [filters.page, filters.limit, filters.sortBy, filters.sortOrder]);

  // Options
  const familyStatusOptions = [
    { value: 'All', label: 'All Status', color: '#6b7280' },
    { value: 'Rich', label: 'Rich', color: '#10b981' },
    { value: 'Middle', label: 'Middle', color: '#3b82f6' },
    { value: 'Poor', label: 'Poor', color: '#f59e0b' },
    { value: 'Solo', label: 'Solo', color: '#ef4444' }
  ];

  const attendanceStatusOptions = [
    { value: 'All', label: 'All', color: '#6b7280' },
    { value: 'Present', label: 'Present', color: '#10b981' },
    { value: 'Absent', label: 'Absent', color: '#ef4444' },
    { value: 'Late', label: 'Late', color: '#f59e0b' }
  ];

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="attendance-records-wrapper">
      <Sidebar />
      <main className="attendance-records-main">
        {/* Header */}
        <div className="attendance-records-header">
          <div className="attendance-records-header-left">
            <button 
              className="attendance-records-back-btn"
              onClick={handleBackClick}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back
            </button>
            <div className="attendance-records-title-section">
              <h1 className="attendance-records-title">
                <FontAwesomeIcon icon={faClipboardList} className="attendance-records-title-icon" />
                Attendance Records
              </h1>
              <p className="attendance-records-subtitle">
                Manage and analyze student attendance data
              </p>
            </div>
          </div>
          
          <div
  style={{
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  }}
>
  <button
    onClick={handleResetFilters}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 14px',
      borderRadius: '6px',
      border: '1px solid #ccc',
      backgroundColor: '#f5f5f5',
      color: '#333',
      cursor: 'pointer',
      fontSize: '14px'
    }}
  >
    <FontAwesomeIcon icon={faRefresh} />
    Reset Filters
  </button>

  <button
    onClick={handleApplyFilters}
    disabled={loading}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: loading ? '#9ec5fe' : '#0d6efd',
      color: '#fff',
      cursor: loading ? 'not-allowed' : 'pointer',
      fontSize: '14px',
      opacity: loading ? 0.8 : 1
    }}
  >
    <FontAwesomeIcon icon={faSearch} spin={loading} />
    {loading ? 'Searching...' : 'Search'}
  </button>
</div>


        </div>

        {/* Main Content */}
        <div className="attendance-records-content">
          {/* Charts Section */}
          <div className="attendance-records-charts-section">
            <div className="attendance-records-stat-cards">
              <div className="attendance-records-stat-card">
                <div className="attendance-records-stat-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="attendance-records-stat-content">
                  <h3>Total Students</h3>
                  <p className="attendance-records-stat-value">
                    {statistics.total?.unique_students || '0'}
                  </p>
                </div>
              </div>
              
              <div className="attendance-records-stat-card">
                <div className="attendance-records-stat-icon success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div className="attendance-records-stat-content">
                  <h3>Attendance Rate</h3>
                  <p className="attendance-records-stat-value">
                    {statistics.total?.attendance_rate ? `${statistics.total.attendance_rate}%` : '0%'}
                  </p>
                </div>
              </div>
              
              <div className="attendance-records-stat-card">
                <div className="attendance-records-stat-icon warning">
                  <FontAwesomeIcon icon={faTimesCircle} />
                </div>
                <div className="attendance-records-stat-content">
                  <h3>Total Absences</h3>
                  <p className="attendance-records-stat-value">
                    {statistics.total?.total_absences || '0'}
                  </p>
                </div>
              </div>
              
              <div className="attendance-records-stat-card">
                <div className="attendance-records-stat-icon info">
                  <FontAwesomeIcon icon={faSchool} />
                </div>
                <div className="attendance-records-stat-content">
                  <h3>Active Classes</h3>
                  <p className="attendance-records-stat-value">
                    {statistics.total?.total_classes || '0'}
                  </p>
                </div>
              </div>
            </div>

            <div className="attendance-records-charts">
              <div className="attendance-records-chart-card">
                <div className="attendance-records-chart-header">
                  <h3>
                    <FontAwesomeIcon icon={faChartLine} />
                    Daily Absence Trend
                  </h3>
                  <span>Last 7 days</span>
                </div>
                <div className="attendance-records-chart-container">
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={chartData.dailyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="absences" 
                        stroke="#ef4444" 
                        fill="#ef4444" 
                        fillOpacity={0.3} 
                        name="Absences"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div className="attendance-records-chart-card">
                <div className="attendance-records-chart-header">
                  <h3>
                    <FontAwesomeIcon icon={faChartPie} />
                    Class Distribution
                  </h3>
                  <span>Absences by class</span>
                </div>
                <div className="attendance-records-chart-container">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={chartData.classDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.classDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} absences`, 'Count']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="attendance-records-filters-section">
            <div className="attendance-records-filters-header">
              <h3>
                <FontAwesomeIcon icon={faFilter} />
                Filter Records
              </h3>
              <button 
                className="attendance-records-clear-filters"
                onClick={handleResetFilters}
              >
                <FontAwesomeIcon icon={faFilterCircleXmark} />
                Clear All
              </button>
            </div>
            
            <div className="attendance-records-filters-grid">
              <div className="attendance-records-filter-group">
                <label>Class</label>
                <select 
                  value={filters.class}
                  onChange={(e) => handleFilterChange('class', e.target.value)}
                >
                  <option value="">All Classes</option>
                  {availableClasses.map((cls, index) => (
                    <option key={index} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
              
              <div className="attendance-records-filter-group">
                <label>Section</label>
                <select 
                  value={filters.section}
                  onChange={(e) => handleFilterChange('section', e.target.value)}
                  disabled={!filters.class}
                >
                  <option value="">All Sections</option>
                  {availableSections.map((section, index) => (
                    <option key={index} value={section}>{section}</option>
                  ))}
                </select>
              </div>
              
              <div className="attendance-records-filter-group">
                <label>Date Range</label>
                <div className="attendance-records-date-range">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="attendance-records-filter-group">
                <label>Student ID</label>
                <input
                  type="text"
                  placeholder="Enter student ID"
                  value={filters.studentId}
                  onChange={(e) => handleFilterChange('studentId', e.target.value)}
                />
              </div>
              
              <div className="attendance-records-filter-group">
                <label>Student Name</label>
                <input
                  type="text"
                  placeholder="Search by name"
                  value={filters.studentName}
                  onChange={(e) => handleFilterChange('studentName', e.target.value)}
                />
              </div>
              
              <div className="attendance-records-filter-group">
                <label>Family Status</label>
                <div className="attendance-records-status-tags">
                  {familyStatusOptions.map((status) => (
                    <button
                      key={status.value}
                      className={`attendance-records-status-tag ${filters.familyStatus === status.value ? 'active' : ''}`}
                      onClick={() => handleFilterChange('familyStatus', status.value)}
                      style={filters.familyStatus === status.value ? { backgroundColor: status.color } : {}}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="attendance-records-table-section">
            <div className="attendance-records-table-header">
              <div className="attendance-records-table-header-left">
                <h3>
                  <FontAwesomeIcon icon={faClipboardList} />
                  Attendance Records ({totalRecords})
                </h3>
                <div className="attendance-records-selected-count">
                  {selectedRecords.length > 0 && (
                    <span>{selectedRecords.length} selected</span>
                  )}
                </div>
              </div>
              
              <div className="attendance-records-table-header-right">
                <div className="attendance-records-export-actions">
                  <button 
                    className="attendance-records-export-btn"
                    onClick={exportToPDF}
                    disabled={exporting || records.length === 0}
                    title="Generate Professional PDF Report"
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    {exporting ? 'Generating...' : 'Professional PDF'}
                  </button>
                  <button 
                    className="attendance-records-export-btn"
                    onClick={exportToExcel}
                    disabled={records.length === 0}
                  >
                    <FontAwesomeIcon icon={faFileExcel} />
                    Excel
                  </button>
                  <button 
                    className="attendance-records-export-btn"
                    onClick={() => window.print()}
                    disabled={records.length === 0}
                  >
                    <FontAwesomeIcon icon={faPrint} />
                    Print
                  </button>
                </div>
              </div>
            </div>

            <div className="attendance-records-table-container">
              {loading ? (
                <div className="attendance-records-loading">
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p>Loading attendance records...</p>
                </div>
              ) : records.length === 0 ? (
                <div className="attendance-records-empty">
                  <FontAwesomeIcon icon={faDatabase} size="3x" />
                  <h3>No records found</h3>
                  <p>Try adjusting your search filters</p>
                </div>
              ) : (
                <div className="attendance-records-table-wrapper">
                  <table className="attendance-records-table">
                    <thead>
                      <tr>
                        <th className="attendance-records-table-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedRecords.length === records.length && records.length > 0}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th onClick={() => handleSort('student_id')}>
                          Student ID
                          <FontAwesomeIcon icon={getSortIcon('student_id')} />
                        </th>
                        <th onClick={() => handleSort('name')}>
                          Student Name
                          <FontAwesomeIcon icon={getSortIcon('name')} />
                        </th>
                        <th>Father's Name</th>
                        <th onClick={() => handleSort('class')}>
                          Class
                          <FontAwesomeIcon icon={getSortIcon('class')} />
                        </th>
                        <th>Section</th>
                        <th>Family Status</th>
                        <th onClick={() => handleSort('marked_at')}>
                          Date
                          <FontAwesomeIcon icon={getSortIcon('marked_at')} />
                        </th>
                        <th>Actions</th>
                        <th>Day</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((record) => (
                        <tr 
                          key={record.id}
                          className={selectedRecords.includes(record.id) ? 'selected' : ''}
                        >
                          <td className="attendance-records-table-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => toggleRecordSelection(record.id)}
                            />
                          </td>
                          <td>
                            <div className="attendance-records-student-id">
                              <FontAwesomeIcon icon={faIdCard} />
                              {record.student_id}
                            </div>
                          </td>
                          <td>
                            <div className="attendance-records-student-info">
                              <div className="attendance-records-student-name">{record.name}</div>
                            </div>
                          </td>
                          <td>{record.father_name || 'N/A'}</td>
                          <td>
                            <span className="attendance-records-class-badge">
                              {record.class}
                            </span>
                          </td>
                          <td>{record.section || 'N/A'}</td>
                          <td>
                            <span className={`attendance-records-family-status-badge ${record.family_status?.toLowerCase() || 'unknown'}`}>
                              {record.family_status || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="attendance-records-date-cell">
                              <FontAwesomeIcon icon={faCalendar} />
                              {formatDisplayDate(record.marked_at)}
                            </div>
                          </td>
                          <td>
                            <div className="attendance-records-action-buttons">
                              <button 
                                className="attendance-records-action-btn view"
                                onClick={() => openViewModal(record)}
                                title="View Details"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button 
                                className="attendance-records-action-btn mark-present"
                                onClick={() => openMarkPresentModal(record)}
                                title="Mark as Present"
                              >
                                <FontAwesomeIcon icon={faUserCheck} />
                              </button>
                            </div>
                          </td>
                          <td>
                            <span className="attendance-records-day-badge">
                              {record.day}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {records.length > 0 && (
                <div className="attendance-records-pagination">
                  <div className="attendance-records-pagination-info">
                    Showing {((filters.page - 1) * filters.limit) + 1} to {Math.min(filters.page * filters.limit, totalRecords)} of {totalRecords} entries
                  </div>
                  <div className="attendance-records-pagination-controls">
                    <button 
                      className="attendance-records-pagination-btn"
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                      Previous
                    </button>
                    
                    <div className="attendance-records-pagination-numbers">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (filters.page <= 3) {
                          pageNum = i + 1;
                        } else if (filters.page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = filters.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            className={`attendance-records-pagination-btn ${filters.page === pageNum ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button 
                      className="attendance-records-pagination-btn"
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page === totalPages}
                    >
                      Next
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                  
                  <div className="attendance-records-page-size">
                    <select 
                      value={filters.limit}
                      onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                    >
                      <option value="10">10 per page</option>
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* View Details Modal */}
        {viewModalOpen && selectedRecord && (
          <div className="attendance-records-modal">
            <div className="attendance-records-modal-content">
              <div className="attendance-records-modal-header">
                <h3>Student Record Details</h3>
                <button 
                  className="attendance-records-modal-close"
                  onClick={() => setViewModalOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="attendance-records-modal-body">
                <div className="attendance-records-details-grid">
                  <div className="attendance-records-detail-item">
                    <label>Student ID:</label>
                    <span>{selectedRecord.student_id}</span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Student Name:</label>
                    <span>{selectedRecord.name}</span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Father's Name:</label>
                    <span>{selectedRecord.father_name || 'N/A'}</span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Class & Section:</label>
                    <span>{selectedRecord.class} - {selectedRecord.section || 'N/A'}</span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Date:</label>
                    <span>{formatDisplayDate(selectedRecord.marked_at)} ({selectedRecord.day})</span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Mobile:</label>
                    <span>{selectedRecord.mobile || 'N/A'}</span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Family Status:</label>
                    <span className={`attendance-records-family-status-badge ${selectedRecord.family_status?.toLowerCase()}`}>
                      {selectedRecord.family_status || 'N/A'}
                    </span>
                  </div>
                  <div className="attendance-records-detail-item">
                    <label>Marked At:</label>
                    <span>{new Date(selectedRecord.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="attendance-records-modal-footer">
                <button 
                  className="attendance-records-btn secondary"
                  onClick={() => setViewModalOpen(false)}
                >
                  Close
                </button>
                <button 
                  className="attendance-records-btn primary"
                  onClick={() => {
                    setViewModalOpen(false);
                    openMarkPresentModal(selectedRecord);
                  }}
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                  Mark as Present
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModalOpen && recordToMark && (
          <div className="attendance-records-modal">
            <div className="attendance-records-modal-content confirm">
              <div className="attendance-records-modal-header">
                <h3>Confirm Action</h3>
                <button 
                  className="attendance-records-modal-close"
                  onClick={() => setConfirmModalOpen(false)}
                >
                  ×
                </button>
              </div>
              <div className="attendance-records-modal-body">
                <div className="attendance-records-confirmation-content">
                  <div className="attendance-records-confirmation-icon">
                    <FontAwesomeIcon icon={faUserCheck} />
                  </div>
                  <h4>Mark Student as Present?</h4>
                  <p>This will remove the absence record for:</p>
                  <div className="attendance-records-confirmation-details">
                    <p><strong>Student:</strong> {recordToMark.name}</p>
                    <p><strong>ID:</strong> {recordToMark.student_id}</p>
                    <p><strong>Class:</strong> {recordToMark.class} - {recordToMark.section || 'N/A'}</p>
                    <p><strong>Date:</strong> {formatDisplayDate(recordToMark.marked_at)}</p>
                  </div>
                  <p className="attendance-records-warning-text">
                    This action cannot be undone. The record will be permanently deleted from absent students.
                  </p>
                </div>
              </div>
              <div className="attendance-records-modal-footer">
                <button 
                  className="attendance-records-btn secondary"
                  onClick={() => setConfirmModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  className="attendance-records-btn danger"
                  onClick={confirmMarkPresent}
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                  Yes, Mark as Present
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Loading Overlay */}
        {exporting && (
          <div className="attendance-records-loading-overlay">
            <div className="attendance-records-loading-content">
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
              <h3>Generating Professional PDF Report</h3>
              <p>Creating A4 format document with executive summary...</p>
              <div className="attendance-records-progress-steps">
                <div className="progress-step active">✓ Adding headers</div>
                <div className="progress-step active">✓ Creating summary</div>
                <div className="progress-step">Generating tables</div>
                <div className="progress-step">Adding watermarks</div>
                <div className="progress-step">Finalizing document</div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AttendanceRecords;